import * as React from 'react';
import { BadgeCheck, Printer, X } from 'lucide-react';
import type { useCracha } from '@/hooks/useCracha';
import { cn } from '@/lib/utils';
import { Botao } from '@/componentes/ui/botao';
import { Campo, Cartao, CorpoCartao } from '@/componentes/ui/primitivos';

/**
 * O crachá de quem está operando, na tela.
 *
 * Mora aqui, e não dentro de uma página, porque o crachá não é assunto de uma
 * tela só: ele assina a NF-e no faturamento e assina a conferência — nas duas
 * o ERP grava o número, e nas duas a pergunta ao usuário tem de ser a mesma.
 * Enquanto estes dois componentes viviam em `Faturamento.tsx`, a conferência
 * ou ficaria sem crachá ou ganharia uma segunda versão da mesma pergunta, com
 * outro texto — que é como duas telas passam a ensinar coisas diferentes
 * sobre o mesmo cadastro.
 */
export function SeloCracha({ cracha }: { cracha: ReturnType<typeof useCracha> }): JSX.Element | null {
  const [trocando, definirTrocando] = React.useState(false);
  const [valor, definirValor] = React.useState('');
  if (cracha.cracha === null) return null;

  if (trocando) {
    return (
      <form
        className="flex items-center gap-1.5"
        onSubmit={(e) => {
          e.preventDefault();
          if (valor.trim()) cracha.informar(valor.trim());
          definirTrocando(false);
          definirValor('');
        }}
      >
        <Campo
          value={valor}
          onChange={(e) => definirValor(e.target.value.replace(/\D/g, ''))}
          inputMode="numeric"
          autoFocus
          placeholder="crachá"
          aria-label="Usar outro crachá nesta sessão"
          className="tabular w-[110px] font-mono"
        />
        <Botao variante="primaria" tamanho="pequeno" type="submit" disabled={valor.trim() === ''}>
          Usar
        </Botao>
        <button
          type="button"
          onClick={() => definirTrocando(false)}
          className="text-textoFraco hover:text-texto"
          aria-label="Cancelar troca"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </form>
    );
  }

  const trocado = !cracha.daSessao;
  return (
    <span
      className="flex items-center gap-1.5 rounded-lg border border-borda bg-superficie px-2.5 py-1.5"
      title={
        cracha.daSessao
          ? 'Crachá vinculado ao seu usuário no cadastro do Procfit'
          : 'Crachá só desta sessão — o cadastro do Procfit não muda. Some ao sair.'
      }
    >
      <BadgeCheck
        className={cn('h-4 w-4', trocado ? 'text-status-atencao' : 'text-status-livre')}
      />
      <span className="tabular font-mono text-[13px] font-bold">{cracha.cracha}</span>
      {cracha.impressora ? (
        <span className="flex items-center gap-1 text-[11px] text-textoFraco">
          <Printer className="h-3.5 w-3.5" />
          {cracha.impressora}
        </span>
      ) : null}
      {trocado && cracha.doCadastro ? (
        <button
          type="button"
          onClick={() => cracha.informar(null)}
          className="text-[11px] text-textoFraco underline hover:text-texto"
        >
          voltar para o {cracha.doCadastro}
        </button>
      ) : null}
      <button
        type="button"
        onClick={() => definirTrocando(true)}
        className="text-[11px] text-textoFraco underline hover:text-texto"
      >
        trocar
      </button>
    </span>
  );
}

/**
 * Quando o usuário não tem crachá no cadastro.
 *
 * Bloqueia a tela de propósito: sem crachá o ERP não sabe quem assinou — e
 * conferência sem assinatura é conferência de ninguém. Mas não manda "procure
 * o administrador" e para por aí: quem está na bancada precisa trabalhar
 * agora.
 */
export function PedirCracha({
  aoInformar,
  motivo,
}: {
  aoInformar: (c: string) => void;
  /** O que o crachá assina NESTA tela. Muda a frase, não a regra. */
  motivo?: string;
}): JSX.Element {
  const [valor, definirValor] = React.useState('');
  return (
    <Cartao>
      <CorpoCartao className="space-y-3">
        <div className="flex items-start gap-3">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-status-atencaoFundo text-status-atencao">
            <BadgeCheck className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <h2 className="text-[15px] font-bold">Qual é o seu crachá?</h2>
            <p className="mt-1 text-[12.5px] leading-relaxed text-textoFraco">
              O seu usuário ainda não tem crachá vinculado no cadastro.{' '}
              {motivo ?? 'É o crachá que assina a NF-e e define quais pedidos são da sua fila.'}{' '}
              Informe agora para trabalhar — vale só nesta sessão. Para ele vir sozinho, em
              qualquer máquina, o vínculo é feito no cadastro de crachás do Procfit.
            </p>
          </div>
        </div>
        <form
          className="flex flex-wrap items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (valor.trim()) aoInformar(valor.trim());
          }}
        >
          <Campo
            value={valor}
            onChange={(e) => definirValor(e.target.value.replace(/\D/g, ''))}
            inputMode="numeric"
            autoFocus
            placeholder="número do crachá"
            aria-label="Número do crachá"
            className="tabular w-[180px] font-mono"
          />
          <Botao variante="primaria" type="submit" disabled={valor.trim() === ''}>
            Usar este crachá
          </Botao>
        </form>
      </CorpoCartao>
    </Cartao>
  );
}
